'use client';

import { useEffect, useMemo, useRef } from 'react';
import { sanitizeInlineHtml } from '@/lib/sanitize-inline-html';

/**
 * Renders an inline ```html ... ``` block from the assistant's reply as
 * raw HTML mounted directly into the chat DOM. No iframe.
 *
 * - **Theme inheritance** — the block sits inside the chat's DOM tree, so
 *   it inherits the surrounding background, font, color scheme, and any
 *   CSS custom properties. Tailwind classes the app already loads work
 *   natively, no extra CSS fetch needed.
 * - **Streaming** — partial / unclosed HTML coming from the model gets
 *   rendered as it arrives. Browsers tolerate malformed mid-stream HTML
 *   (they auto-close tags), so the user sees structure appear
 *   incrementally instead of staring at a code-fenced block until the
 *   closing ``` arrives.
 * - **Scripts** — `dangerouslySetInnerHTML` does NOT auto-execute
 *   <script> tags. We re-create them after a 350ms debounce — long
 *   enough to outlast a typical streaming pause, short enough to feel
 *   instant when the model lands the final chunk. Debounce avoids
 *   firing the same script on every keystroke during streaming.
 * - **Sanitization** — see lib/sanitize-inline-html.ts. Strips
 *   <iframe>/<object>/<embed>/<meta>/<link>/<base>, neutralizes
 *   javascript: URLs, drops CSS rules targeting global element
 *   selectors (html/body/*).
 */
export function InlineHtmlBlock({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sanitized = useMemo(() => sanitizeInlineHtml(html), [html]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    // Debounce script execution. Streaming HTML calls this effect on
    // every chunk; firing scripts on every chunk would (a) re-run side
    // effects, (b) freeze the chat on heavy scripts, (c) flicker
    // canvas/animation setups. Wait 350ms after the last content change
    // — if streaming is ongoing, the timer keeps resetting.
    const t = setTimeout(() => {
      const scripts = Array.from(root.querySelectorAll('script:not([data-executed="true"])'));
      for (const oldScript of scripts) {
        const newScript = document.createElement('script');
        for (const attr of Array.from(oldScript.attributes)) {
          newScript.setAttribute(attr.name, attr.value);
        }
        newScript.setAttribute('data-executed', 'true');
        newScript.text = oldScript.textContent ?? '';
        oldScript.parentNode?.replaceChild(newScript, oldScript);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [sanitized]);

  return (
    <div
      ref={containerRef}
      className="inline-html-block my-3"
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
